import React, { useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  TextField,
  Stack,
  Chip,
  Box,
  Grid,
  InputAdornment,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import QueryPresetCard from "./QueryPresetCard";
import { filterPresets } from "../../utils/queryBuilderPresets";

const buildCategories = (presets) => {
  const seen = new Set();
  const cats = [{ id: "all", label: "All" }];
  presets.forEach((p) => {
    if (p.category && !seen.has(p.category)) {
      seen.add(p.category);
      const label = p.category.charAt(0).toUpperCase() + p.category.slice(1);
      cats.push({ id: p.category, label });
    }
  });
  return cats;
};

const QueryPresetGalleryDialog = ({
  open,
  onClose,
  onSelect,
  activePresetId,
  presets = [],
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");

  const categories = useMemo(() => buildCategories(presets), [presets]);

  const visiblePresets = useMemo(
    () => filterPresets(presets, { category, search }),
    [presets, category, search]
  );

  const handleSelect = (preset) => {
    onSelect(preset);
    onClose();
    setSearch("");
    setCategory("all");
  };

  const handleClose = () => {
    onClose();
    setSearch("");
    setCategory("all");
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={fullScreen}
      maxWidth="lg"
      fullWidth
      scroll="paper"
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
          <Box>
            <Typography variant="h5" fontWeight={800}>
              Query library
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {presets.length} curated searches — pick one to load filters and run instantly.
            </Typography>
          </Box>
          <IconButton onClick={handleClose} aria-label="Close query library">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ bgcolor: "background.default" }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search queries (500 HR, Yankees, season, OBP...)"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 2.5 }}>
          {categories.map((item) => (
            <Chip
              key={item.id}
              label={item.label}
              clickable
              color={category === item.id ? "primary" : "default"}
              variant={category === item.id ? "filled" : "outlined"}
              onClick={() => setCategory(item.id)}
            />
          ))}
        </Stack>

        {visiblePresets.length === 0 ? (
          <Box sx={{ py: 8, textAlign: "center" }}>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
              No queries match
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Try another category or search term.
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={1.5}>
            {visiblePresets.map((preset) => (
              <Grid item xs={12} sm={6} md={4} key={preset.id}>
                <QueryPresetCard
                  preset={preset}
                  selected={activePresetId === preset.id}
                  onClick={handleSelect}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default QueryPresetGalleryDialog;
